import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePartnerSessionStore } from "../lib/partnerAuth";
import {
  computeStageStats,
  type PartnerOrderRow,
  type PartnerOrderStage,
} from "../lib/partnerOrders";
import { isInDateRange, type PartnerDashboardDateRange } from "../lib/partnerDashboardDate";
import {
  usePartnerDrivers,
  usePartnerFallbackEnabled,
  usePartnerOrdersList,
} from "../hooks/usePartnerOrders";
import { usePartnerWalletV2 } from "../hooks/usePartnerWallets";
import { usePartnerDashboardDateRange } from "../hooks/usePartnerDashboardDateRange";
import { useI18n } from "../i18n";
import { ROUTES } from "../routes";
import {
  PartnerLiveOrderFlow,
  PARTNER_FLOW_STAGES,
  type PartnerFlowStepCount,
} from "../components/dashboard/PartnerLiveOrderFlow";
import { PartnerRecentOrders } from "../components/dashboard/PartnerRecentOrders";
import { PartnerActiveClients } from "../components/dashboard/PartnerActiveClients";
import { OverviewDateControl } from "../components/dashboard/OverviewDateControl";
import { DispatchIntelligence } from "../components/dashboard/DispatchIntelligence";

const LIVE_STAGES = new Set<PartnerOrderStage>([
  "pending",
  "processing",
  "ready",
  "in_delivery",
]);

/** Active pipeline always visible; terminal stages respect the date range. */
function forDashboardView(
  orders: PartnerOrderRow[],
  range: PartnerDashboardDateRange
): PartnerOrderRow[] {
  return orders.filter((o) => {
    if (LIVE_STAGES.has(o.stage)) return true;
    return isInDateRange(o.created_at, range);
  });
}

/**
 * Partner HQ Dashboard — live data + Client Overview chrome (flattened).
 */
export default function PartnerHomePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const partnerName =
    usePartnerSessionStore((s) => s.session?.partnerName) ||
    t("overview.titleFallback");
  const [selectedStage, setSelectedStage] = useState<PartnerOrderStage | null>(
    null
  );
  const { range, setToday, setCustom } = usePartnerDashboardDateRange();

  const ordersQuery = usePartnerOrdersList();
  const driversQuery = usePartnerDrivers();
  const fallbackQuery = usePartnerFallbackEnabled();
  const walletQuery = usePartnerWalletV2();

  const allOrders = ordersQuery.data?.orders ?? [];
  const drivers = driversQuery.data ?? [];
  const fallbackEnabled = fallbackQuery.data === true;

  const viewOrders = useMemo(
    () => forDashboardView(allOrders, range),
    [allOrders, range]
  );

  const flowSteps: PartnerFlowStepCount[] = useMemo(() => {
    const stats = computeStageStats(viewOrders);
    return PARTNER_FLOW_STAGES.map((stage) => ({
      stage,
      count:
        stage === "pending"
          ? stats.pending
          : stage === "processing"
            ? stats.processing
            : stage === "ready"
              ? stats.ready
              : stage === "in_delivery"
                ? stats.in_delivery
                : stats.completed,
    }));
  }, [viewOrders]);

  const filteredOrders = useMemo(() => {
    const live = viewOrders.filter((o) => o.stage !== "cancelled");
    if (!selectedStage) return live.slice(0, 20);
    return live.filter((o) => o.stage === selectedStage).slice(0, 20);
  }, [viewOrders, selectedStage]);

  return (
    <div className="menu-page overview-page">
      <header className="menu-page__header overview-page__header">
        <div className="min-w-0">
          <p className="menu-page__eyebrow">{t("overview.eyebrow")}</p>
          <h1 className="menu-page__title overview-page__title">{partnerName}</h1>
          <p className="menu-page__subtitle overview-page__subtitle">
            {t("overview.subtitle")}
          </p>
        </div>
        <div className="menu-page__header-actions overview-page__header-actions">
          <OverviewDateControl
            range={range}
            onToday={setToday}
            onCustom={setCustom}
            compact
          />
        </div>
      </header>

      {ordersQuery.isError ? (
        <div className="overview-page__error" role="alert">
          <p>{t("overview.loadError")}</p>
        </div>
      ) : null}

      <PartnerLiveOrderFlow
        steps={flowSteps}
        selectedStage={selectedStage}
        onSelectStage={setSelectedStage}
        loading={ordersQuery.isLoading && !ordersQuery.data}
      />

      <DispatchIntelligence
        drivers={drivers}
        orders={viewOrders}
        fallbackEnabled={fallbackEnabled}
        wallet={walletQuery.data?.wallet ?? null}
        loading={
          (driversQuery.isLoading && !driversQuery.data) ||
          (ordersQuery.isLoading && !ordersQuery.data) ||
          (walletQuery.isLoading && !walletQuery.data)
        }
      />

      <div className="overview-page__grid">
        <PartnerRecentOrders
          orders={filteredOrders}
          filterStage={selectedStage}
          onClearFilter={() => setSelectedStage(null)}
          onOrderTap={(order) =>
            navigate(`${ROUTES.ORDERS}?id=${encodeURIComponent(order.id)}`)
          }
        />
        <PartnerActiveClients />
      </div>
    </div>
  );
}
